import React, { useState, useEffect, useRef } from 'react';
import { brandContent } from '../data/mock';
import { selectHeroAsset } from '../utils/scrollAnimations';
import Timer from './Timer';

/**
 * HeroSection — scroll-jacked parallax zoom with staggered text reveal.
 *
 * Container height = 100vh + SCROLL_RANGE.
 * The sticky inner panel stays pinned while the container scrolls past,
 * driving progress 0→1 which controls:
 *   - Background zoom: scale(1) → scale(1.375)
 *   - Text stagger: tagline → "Launching in" → Timer, each sliding up in sequence
 */
const SCROLL_RANGE = 800;

// Each text element occupies a slice of the 0→1 progress range
const TEXT_PHASES = [
  { start: -0.1, end: 0.20 },  // tagline (starts slightly visible)
  { start: 0.15, end: 0.45 },  // "Launching in"
  { start: 0.35, end: 0.65 }, // Timer
];

function phaseProgress(progress, start, end) {
  return Math.min(1, Math.max(0, (progress - start) / (end - start)));
}

const HeroSection = () => {
  const containerRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [activeAsset, setActiveAsset] = useState(() =>
    selectHeroAsset(window.innerWidth)
  );
  const [isLaunched] = useState(Date.now() >= brandContent.launchTimestamp);

  // Responsive asset swap
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = () => setActiveAsset(selectHeroAsset(window.innerWidth));
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Scroll-jacked progress
  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) { setProgress(1); return; }

    let rafHandle = null;
    const onScroll = () => {
      if (rafHandle) return;
      rafHandle = requestAnimationFrame(() => {
        rafHandle = null;
        if (!containerRef.current) return;
        const { top } = containerRef.current.getBoundingClientRect();
        const scrolled = Math.max(0, -top);
        setProgress(Math.min(1, scrolled / SCROLL_RANGE));
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafHandle) cancelAnimationFrame(rafHandle);
    };
  }, []);

  const scale = 1 + 0.375 * progress;

  // Per-element animation values
  const p0 = phaseProgress(progress, TEXT_PHASES[0].start, TEXT_PHASES[0].end);
  const p1 = phaseProgress(progress, TEXT_PHASES[1].start, TEXT_PHASES[1].end);
  const p2 = phaseProgress(progress, TEXT_PHASES[2].start, TEXT_PHASES[2].end);

  const textStyle = (p) => ({
    opacity: p,
    transform: `translateY(${(1 - p) * 32}px)`,
    willChange: 'transform, opacity',
  });

  // Calculate reveal for the tagline specifically (half-visible at start)
  // At progress=0, p0 is ~0.33. We want translateY to be around 50-60% to show half the text.
  // We'll use a dedicated reveal percentage for the tagline.
  const taglineReveal = Math.min(1, 0.5 + progress * 2.5); // Starts at 0.5, hits 1 quickly

  return (
    <div
      ref={containerRef}
      style={{ height: `calc(100vh + ${SCROLL_RANGE}px)` }}
      className="relative"
    >
      <div className="sticky top-0 h-screen overflow-hidden bg-primary-navy">

        {/* Parallax background */}
        <div
          className="absolute inset-0 bg-cover max-md:bg-[40%_center] md:bg-center"
          style={{
            backgroundImage: `url(${activeAsset})`,
            transform: `scale(${scale}) translateZ(0)`,
            willChange: 'transform',
            transformOrigin: 'center center',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
          }}
        />

        {/* Subtle dark overlay for legibility */}
        <div className="absolute inset-0 bg-primary-navy/35" />

        {/* Bottom fade — blends into ZurianStandard below */}
        <div className="absolute bottom-0 left-0 w-full h-40 bg-gradient-to-t from-primary-navy to-transparent pointer-events-none" />


        {/* Staggered text content */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center pt-16 sm:pt-20 pb-12 sm:pb-24 text-center px-4 sm:px-6">
          <div className="flex flex-col items-center w-full max-w-4xl">

            {/* Text 1 — tagline (Intelligent EARTH Reveal: Aligned & Optimized) */}
            <div 
              style={{
                ...textStyle(p0),
                opacity: Math.max(0.4, p0)
              }}
              className="relative mt-2 sm:mt-4 mb-12 sm:mb-20"
            >
              <h2 className="font-body text-[2.25rem] sm:text-[3rem] md:text-[3.25rem] leading-none tracking-[0.12em] font-normal drop-shadow-[0_4px_10px_rgba(0,0,0,0.6)] flex flex-wrap justify-center items-center">
                {"THE WISER WEALTH".split(' ').map((word, wordIndex) => (
                  <span key={wordIndex} className="inline-flex overflow-hidden relative" style={{ marginRight: wordIndex < 2 ? '0.45em' : '0' }}>
                    {word.split('').map((char, charIndex) => {
                      // Selective Plating Logic: One specific set to spell "EARTH"
                      // Word 0 (THE): Ind 2 (E)
                      // Word 1 (WISER): Ind 4 (R)
                      // Word 2 (WEALTH): Ind 2 (A), Ind 4 (T), Ind 5 (H)
                      const isGoldTarget = 
                        (wordIndex === 0 && charIndex === 2) || 
                        (wordIndex === 1 && charIndex === 4) || 
                        (wordIndex === 2 && (charIndex === 2 || charIndex === 4 || charIndex === 5));
                      
                      // Plating starts at 26% and finishes by 50% scroll
                      // This creates the "smooth downwards flow" on each letter
                      const platingReveal = Math.min(1, Math.max(0, (progress - 0.26) / 0.24));
                      
                      return (
                        <span 
                          key={charIndex}
                          className={`char-box ${isGoldTarget ? 'char-gold' : 'char-silver'}`}
                          style={{
                            transform: `translateY(${(1 - taglineReveal) * 100}%)`,
                            '--plating-reveal': isGoldTarget ? platingReveal : 0,
                          }}
                        >
                          {char}
                        </span>
                      );
                    })}
                  </span>
                ))}
              </h2>
              
              {/* Silver Underline (Foundation) — expands as you scroll */}
              <div 
                className="h-[1px] bg-gradient-to-r from-transparent via-white/40 to-transparent w-full mt-2 transition-all duration-1000 ease-out origin-center"
                style={{
                  transform: `scaleX(${progress > 0.1 ? 0.9 : 0.35})`,
                  opacity: 0.3 + (progress * 0.4),
                  boxShadow: progress > 0.2 ? '0 0 10px rgba(255, 255, 255, 0.1)' : 'none'
                }}
              />
            </div>

            {/* Launch CTA */}
            <div className="flex flex-col items-center">
              <div style={textStyle(p2)} className="w-full flex justify-center scale-90 sm:scale-100">
                <Timer launchTimestamp={brandContent.launchTimestamp} />
              </div>
            </div>

          </div>

          {/* Craftsmanship quote — fades in last */}
          <div
            className="absolute bottom-[22px] w-[95%] text-center px-2"
            style={textStyle(phaseProgress(progress, 0.55, 0.8))}
          >
            <p className="font-heading text-[12px] sm:text-sm md:text-base text-secondary-tan font-medium leading-[1.4] drop-shadow-sm">
              "Born from a 35+ year legacy of craftsmanship and trust. Precious Metal - SILVER, priced with honesty &ndash; crafted to build Shining Memories."
            </p>
          </div>
        </div>

        {/* Scroll Indication — Fades out as you scroll */}
        <div 
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-3 transition-opacity duration-300 select-none"
          style={{ 
            opacity: Math.max(0, 1 - progress * 5),
            pointerEvents: progress > 0.1 ? 'none' : 'auto'
          }}
        >
          <span className="text-[12px] sm:text-[13px] md:text-[14px] lg:text-[15px] tracking-[0.3em] font-body text-primary-beige/80 uppercase text-center w-full">Scroll to Discover</span>
          <div className="w-[2px] h-10 sm:h-12 bg-primary-beige/20 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-t from-primary-beige to-secondary-tan animate-scroll-indicator-reverse" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
